import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CreditCard, MapPin, AlertCircle, Wallet, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '@/services/api';

interface ServiceRequestDetail {
  id: string;
  service_category: string;
  service_type: string;
  contact_name: string;
  contact_email: string;
  quoted_price?: number;
  status: string;
}

interface WalletBalance {
  balance: number;
  currency: string;
}

export default function ServiceRequestPaymentPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [request, setRequest] = useState<ServiceRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);

  const [formData, setFormData] = useState({
    paymentMethod: 'card',
    cardNumber: '',
    cardExpiry: '',
    cardCvv: ''
  });

  useEffect(() => {
    loadServiceRequest();
    loadWalletBalance();
  }, [id]);

  const loadServiceRequest = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await apiService.request(`/service-requests/${id}`, 'GET');
      
      if (response.success) {
        setRequest(response.data);
      } else {
        setError(response.message || 'Failed to load service request');
      }
    } catch (err: any) {
      console.error('Error loading service request:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const response = await apiService.request('/wallet/balance', 'GET');
      if (response.success) {
        setWalletBalance(response.data);
      }
    } catch (err) {
      console.error('Error loading wallet balance:', err);
    }
  };

  const handlePaymentMethodChange = (method: string) => {
    setFormData({ ...formData, paymentMethod: method });
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\s+/g, '');
    value = value.slice(0, 16);
    // Format as groups of 4
    const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    setFormData({ ...formData, cardNumber: formatted });
  };

  const handleCardExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + ' / ' + value.slice(2, 4);
    }
    setFormData({ ...formData, cardExpiry: value });
  };

  const handleCardCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData({ ...formData, cardCvv: value });
  };

  const handlePayment = async () => {
    if (!request?.quoted_price || !id) {
      setError('Invalid payment amount');
      return;
    }

    // Validate card details if card payment is selected
    if (formData.paymentMethod === 'card') {
      const cardNumber = formData.cardNumber.replace(/\s/g, '');
      if (!cardNumber || cardNumber.length !== 16) {
        setError('Please enter a valid 16-digit card number');
        return;
      }
      if (!formData.cardExpiry || formData.cardExpiry.length < 7) {
        setError('Please enter expiry date in MM / YY format');
        return;
      }
      if (!formData.cardCvv || formData.cardCvv.length < 3) {
        setError('Please enter a valid CVV (3-4 digits)');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');

      const amountInPence = Math.round(parseFloat(String(request.quoted_price)) * 1.2 * 100);

      if (formData.paymentMethod === 'wallet') {
        // Wallet payment
        const response = await apiService.request('/service-requests/payment/wallet', 'POST', {
          serviceRequestId: id,
          amountInPence,
        });

        if (response.success) {
          setSuccess(true);
          // Redirect with payment=success param so detail page can verify payment
          setTimeout(() => {
            navigate(`/dashboard/service-requests/${id}?payment=success&session=${response.data.transactionId || 'wallet-' + Date.now()}`);
          }, 1000);
        } else {
          setError(response.message || 'Payment failed');
        }
      } else if (formData.paymentMethod === 'card') {
        // Card payment via Stripe
        const response = await apiService.request('/service-requests/payment/card', 'POST', {
          serviceRequestId: id,
          amountInPence,
          cardNumber: formData.cardNumber.replace(/\s/g, ''),
          cardExpiry: formData.cardExpiry.replace(' / ', ''),
          cardCvv: formData.cardCvv,
          returnUrl: window.location.origin,
        });

        if (response.success && response.data?.checkoutUrl) {
          // Redirect to Stripe
          window.location.href = response.data.checkoutUrl;
        } else {
          setError(response.message || 'Failed to initiate card payment');
        }
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Processing... - Virewire</title>
        </Helmet>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading service request...</p>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error && !request) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Error - Virewire</title>
        </Helmet>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              className="mb-6"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Error Loading Service Request</h1>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <Button onClick={() => navigate('/dashboard')}>
                      Go to Dashboard
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!request?.quoted_price) {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Payment - Virewire</title>
        </Helmet>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              className="mb-6"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">No Quoted Price</h1>
                    <p className="text-gray-600 mb-4">
                      This service request does not have a quoted price yet. Please wait for a quote before proceeding to payment.
                    </p>
                    <Button onClick={() => navigate(`/dashboard/service-requests/${id}`)}>
                      Back to Service Request
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const quotedPrice = parseFloat(String(request.quoted_price));
  const vatAmount = quotedPrice * 0.2;
  const totalAmount = quotedPrice + vatAmount;
  const walletBalancePounds = walletBalance ? walletBalance.balance / 100 : 0;

  return (
    <DashboardLayout>
      <Helmet>
        <title>Payment - Service Request {request.id} - Virewire</title>
      </Helmet>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`/dashboard/service-requests/${id}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Service Request
            </Button>
          </div>

          {/* Success Message */}
          {success && (
            <Card className="mb-6 border-l-4 border-l-green-500 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-lg font-bold text-green-900 mb-1">Payment Successful!</h2>
                    <p className="text-green-800">
                      Your payment has been processed. Redirecting to service request...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Message */}
          {error && (
            <Card className="mb-6 border-l-4 border-l-red-500 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-lg font-bold text-red-900 mb-1">Payment Error</h2>
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Payment Card */}
          <Card className="mb-6">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Confirm Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Service Request Details */}
              <div className="mb-6 pb-6 border-b">
                <h3 className="font-semibold text-gray-900 mb-4">Service Request Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Request ID:</span>
                    <span className="font-mono font-medium">{request.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Type:</span>
                    <span className="capitalize">{request.service_type?.replace('-', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contact Name:</span>
                    <span>{request.contact_name}</span>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="mb-6 pb-6 border-b">
                <h3 className="font-semibold text-gray-900 mb-4">Price Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Amount:</span>
                    <span className="font-medium">£{quotedPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (20%):</span>
                    <span className="font-medium">£{vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-3">
                    <span className="font-semibold text-gray-900">Total Amount:</span>
                    <span className="text-lg font-bold text-blue-600">£{totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Payment Method</h3>
                <div className="space-y-3">
                  {/* Card Payment Option */}
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" style={{
                    borderColor: formData.paymentMethod === 'card' ? '#2563eb' : '#e5e7eb',
                    backgroundColor: formData.paymentMethod === 'card' ? '#f0f9ff' : 'transparent'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="ml-3">
                      <CreditCard className="w-5 h-5 text-blue-600 mb-1" />
                      <p className="font-medium text-gray-900">Pay with Card</p>
                      <p className="text-sm text-gray-600">Visa, Mastercard, and other major cards</p>
                    </div>
                  </label>

                  {/* Wallet Payment Option */}
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" style={{
                    borderColor: formData.paymentMethod === 'wallet' ? '#2563eb' : '#e5e7eb',
                    backgroundColor: formData.paymentMethod === 'wallet' ? '#f0f9ff' : 'transparent'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="wallet"
                      checked={formData.paymentMethod === 'wallet'}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={walletBalancePounds < totalAmount}
                    />
                    <div className="ml-3 flex-1">
                      <Wallet className="w-5 h-5 text-blue-600 mb-1" />
                      <p className="font-medium text-gray-900">Pay with Wallet</p>
                      <p className="text-sm text-gray-600">
                        Available balance: £{walletBalancePounds.toFixed(2)}
                        {walletBalancePounds < totalAmount && (
                          <span className="ml-2 text-red-600">
                            (Insufficient funds - need £{(totalAmount - walletBalancePounds).toFixed(2)} more)
                          </span>
                        )}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Card Details Form - Shows when card payment is selected */}
                {formData.paymentMethod === 'card' && (
                  <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4">Card Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Number
                        </label>
                        <input
                          type="text"
                          placeholder="4111 1111 1111 1111"
                          value={formData.cardNumber}
                          onChange={handleCardNumberChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          maxLength="19"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter your 16-digit card number</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expiry Date
                          </label>
                          <input
                            type="text"
                            placeholder="MM / YY"
                            value={formData.cardExpiry}
                            onChange={handleCardExpiryChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength="7"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            CVV
                          </label>
                          <input
                            type="password"
                            placeholder="***"
                            value={formData.cardCvv}
                            onChange={handleCardCvvChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength="4"
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-4 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Your card details are encrypted and secure
                    </p>
                  </div>
                )}
              </div>

              {/* Payment Button */}
              <Button
                onClick={handlePayment}
                disabled={submitting || (formData.paymentMethod === 'card' && (!formData.cardNumber.replace(/\s/g, '') || formData.cardNumber.replace(/\s/g, '').length !== 16))}
                className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-base"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay £{totalAmount.toFixed(2)}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Security Notice */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                <div className="text-sm">
                  <p className="font-semibold text-blue-900 mb-1">Your payment is secure</p>
                  <p className="text-blue-800">
                    All payments are processed securely through Stripe with industry-standard encryption. Your card information is never stored on our servers.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
